import { Plus, Trash2, UploadCloud } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { adminApi } from '../../lib/api'
import { productImageUrl } from '../../lib/images'
import { supabase } from '../../lib/supabase'
import type { AdminData, AdminProduct, AdminVariant, Gender, ProductImage } from '../../types/commerce'

interface FormState {
  id?: string
  name: string
  short_description: string
  description: string
  price: string
  sale_price: string
  category_id: string
  collection_id: string
  gender: Gender
  composition: string
  care_instructions: string
  active: boolean
  featured: boolean
  is_new: boolean
  images: ProductImage[]
  variants: AdminVariant[]
}

const emptyForm: FormState = {
  name: '', short_description: '', description: '', price: '', sale_price: '', category_id: '', collection_id: '', gender: 'unissex', composition: '', care_instructions: '', active: true, featured: false, is_new: true,
  images: [], variants: []
}

const newVariant = (sku: string): AdminVariant => ({
  size: 'M', color_name: 'Preto', color_hex: '#000000', stock: 0, sku, active: true
})

const fromAdminProduct = (item: AdminProduct): FormState => ({
  id: item.product.id,
  name: item.product.name,
  short_description: item.product.short_description ?? '',
  description: item.product.description ?? '',
  price: String(item.product.price),
  sale_price: item.product.sale_price == null ? '' : String(item.product.sale_price),
  category_id: item.product.category_id ?? '',
  collection_id: item.product.collection_id ?? '',
  gender: item.product.gender,
  composition: item.product.composition ?? '',
  care_instructions: item.product.care_instructions ?? '',
  active: item.product.active,
  featured: item.product.featured,
  is_new: item.product.is_new,
  images: item.images.map(({ storage_path, alt_text, position, is_primary }) => ({ storage_path, alt_text, position, is_primary })),
  variants: item.variants.map(v => ({ id: v.id, size: v.size, color_name: v.color_name, color_hex: v.color_hex, stock: Number(v.stock), sku: v.sku, active: v.active }))
})

export function AdminProductEditor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState<AdminData | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm)
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState('')
  const [generatingSku, setGeneratingSku] = useState(false)

  useEffect(() => {
    let active = true
    setData(null)
    setError('')
    setForm(emptyForm)
    setGeneratingSku(!id)

    const load = async () => {
      try {
        const adminData = await adminApi.data()
        if (!active) return
        setData(adminData)
        if (id) {
          const found = adminData.products.find(x => x.product.id === id)
          if (!found) throw new Error('Produto não encontrado.')
          setForm(fromAdminProduct(found))
        } else {
          const sku = await adminApi.generateVariantSku()
          if (!active) return
          setForm(current => ({
            ...current,
            variants: current.variants.length ? current.variants : [newVariant(sku)]
          }))
        }
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : 'Falha ao carregar o produto.')
      } finally {
        if (active) setGeneratingSku(false)
      }
    }
    void load()
    return () => { active = false }
  }, [id])

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm(current => ({ ...current, [key]: value }))

  const uploadImages = async (files: FileList) => {
    setUploading(true); setError('')
    try {
      const paths: ProductImage[] = []
      for (const file of Array.from(files)) {
        const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg'
        const path = `products/${form.id ?? 'drafts'}/${crypto.randomUUID()}.${ext}`
        const { error: uploadError } = await supabase.storage.from('product-images').upload(path, file, { upsert: false, cacheControl: '3600' })
        if (uploadError) throw uploadError
        paths.push({ storage_path: path, alt_text: form.name || null, position: form.images.length + paths.length, is_primary: form.images.length === 0 && paths.length === 0 })
      }
      set('images', [...form.images, ...paths])
    } catch (err) { setError(err instanceof Error ? err.message : 'Falha no upload.') }
    finally { setUploading(false) }
  }

  const validateSkus = () => {
    if (generatingSku || form.variants.length === 0) {
      throw new Error('Aguarde a geração automática dos SKUs antes de salvar.')
    }
    const submittedSkus = new Set<string>()
    for (const variant of form.variants) {
      const sku = variant.sku?.trim()
      if (!sku) throw new Error('Toda variação precisa ter um SKU. Aguarde a geração automática.')
      if (submittedSkus.has(sku)) {
        throw new Error(`O SKU "${sku}" está repetido. Recarregue a página para gerar novos códigos.`)
      }
      submittedSkus.add(sku)
      const existingProduct = data?.products.find(item =>
        item.variants.some(existing => existing.sku?.trim() === sku && existing.id !== variant.id)
      )
      if (existingProduct) {
        throw new Error(`O SKU "${sku}" já pertence ao produto "${existingProduct.product.name}". Recarregue a página para gerar outro.`)
      }
    }
  }

  const save = async (e: React.FormEvent) => {
    e.preventDefault(); setLoading(true); setError(''); setSaved('')
    try {
      validateSkus()
      const payload = {
        id: form.id,
        name: form.name,
        short_description: form.short_description,
        description: form.description,
        price: form.price,
        sale_price: form.sale_price,
        category_id: form.category_id,
        collection_id: form.collection_id,
        gender: form.gender,
        composition: form.composition,
        care_instructions: form.care_instructions,
        active: form.active,
        featured: form.featured,
        is_new: form.is_new,
        images: form.images.map((image, index) => ({ ...image, position: index })),
        variants: form.variants
      }
      const productId = await adminApi.saveProduct(payload)
      setForm(current => ({ ...current, id: productId }))
      setSaved('Produto salvo com sucesso.')
      if (!id) navigate(`/admin/produtos/${productId}`, { replace: true })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Não foi possível salvar.'
      setError(message.includes('product_variants_sku_key')
        ? 'Não foi possível gerar um SKU exclusivo. Tente novamente para receber um novo código.'
        : message)
    }
    finally { setLoading(false) }
  }

  const updateVariant = (index: number, patch: Partial<AdminVariant>) => set('variants', form.variants.map((variant, idx) => idx === index ? { ...variant, ...patch } : variant))

  const addVariant = async () => {
    setGeneratingSku(true)
    setError('')
    try {
      const sku = await adminApi.generateVariantSku()
      setForm(current => ({ ...current, variants: [...current.variants, newVariant(sku)] }))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível gerar o SKU.')
    } finally {
      setGeneratingSku(false)
    }
  }

  return <div className="admin-page"><div className="admin-page__heading"><div><span>CATÁLOGO</span><h1>{form.id ? 'Editar produto' : 'Novo produto'}</h1></div></div>{error && <div className="form-error">{error}</div>}{saved && <div className="form-success">{saved}</div>}<form className="admin-form" onSubmit={save}>
    <section className="admin-panel admin-form__section"><h2>Informações</h2><div className="form-grid"><label>Nome<input required value={form.name} onChange={e => set('name', e.target.value)} /></label><label>Gênero<select value={form.gender} onChange={e => set('gender', e.target.value as Gender)}><option value="unissex">Unissex</option><option value="feminino">Feminino</option><option value="masculino">Masculino</option></select></label></div><label>Descrição curta<input value={form.short_description} onChange={e => set('short_description', e.target.value)} /></label><label>Descrição<textarea rows={5} value={form.description} onChange={e => set('description', e.target.value)} /></label><div className="form-grid"><label>Preço<input required min="0" step="0.01" type="number" value={form.price} onChange={e => set('price', e.target.value)} /></label><label>Preço promocional<input min="0" step="0.01" type="number" value={form.sale_price} onChange={e => set('sale_price', e.target.value)} /></label></div><div className="form-grid"><label>Categoria<select value={form.category_id} onChange={e => set('category_id', e.target.value)}><option value="">Sem categoria</option>{data?.categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label><label>Coleção<select value={form.collection_id} onChange={e => set('collection_id', e.target.value)}><option value="">Sem coleção</option>{data?.collections.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label></div><label>Composição<textarea rows={3} value={form.composition} onChange={e => set('composition', e.target.value)} /></label><label>Cuidados<textarea rows={3} value={form.care_instructions} onChange={e => set('care_instructions', e.target.value)} /></label><div className="check-row"><label><input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} /> Ativo</label><label><input type="checkbox" checked={form.featured} onChange={e => set('featured', e.target.checked)} /> Destaque</label><label><input type="checkbox" checked={form.is_new} onChange={e => set('is_new', e.target.checked)} /> Lançamento</label></div></section>

    <section className="admin-panel admin-form__section"><div className="panel-title-row"><h2>Imagens</h2><label className="button button--outline button--small"><UploadCloud size={16} />{uploading ? 'ENVIANDO...' : 'ADICIONAR'}<input type="file" multiple accept="image/jpeg,image/png,image/webp,image/avif" disabled={uploading} onChange={e => e.target.files && uploadImages(e.target.files)} /></label></div><div className="image-admin-grid">{form.images.map((image, index) => <div className="image-admin-card" key={`${image.storage_path}-${index}`}><img src={productImageUrl(image.storage_path)} alt="" /><div><label><input type="radio" name="primary" checked={image.is_primary} onChange={() => set('images', form.images.map((img, idx) => ({ ...img, is_primary: idx === index })))} /> Principal</label><button type="button" onClick={() => set('images', form.images.filter((_, idx) => idx !== index).map((img, idx) => ({ ...img, is_primary: img.is_primary || (idx === 0 && form.images[index].is_primary) })))}><Trash2 size={15} /> Remover</button></div></div>)}</div></section>

    <section className="admin-panel admin-form__section"><div className="panel-title-row"><h2>Variantes</h2><button type="button" className="button button--outline button--small" disabled={generatingSku || !data} onClick={() => void addVariant()}><Plus size={16} /> {generatingSku ? 'GERANDO SKU...' : 'ADICIONAR'}</button></div><div className="variant-list">{form.variants.map((variant, index) => <div className="variant-row" key={variant.id ?? index}><input aria-label="Tamanho" placeholder="Tamanho" value={variant.size} onChange={e => updateVariant(index, { size: e.target.value })} /><input aria-label="Cor" placeholder="Cor" value={variant.color_name} onChange={e => updateVariant(index, { color_name: e.target.value })} /><input aria-label="Hex" type="color" value={variant.color_hex ?? '#000000'} onChange={e => updateVariant(index, { color_hex: e.target.value })} /><input aria-label="Estoque" min="0" type="number" value={variant.stock} onChange={e => updateVariant(index, { stock: Number(e.target.value) })} /><input aria-label="SKU" title="SKU gerado automaticamente pelo sistema" readOnly value={variant.sku ?? ''} /><label><input type="checkbox" checked={variant.active} onChange={e => updateVariant(index, { active: e.target.checked })} />Ativa</label><button type="button" className="icon-button" onClick={() => set('variants', form.variants.filter((_, idx) => idx !== index))}><Trash2 size={18} /></button></div>)}</div></section>

    <div className="admin-form__actions"><button type="button" className="button button--outline" onClick={() => navigate('/admin/produtos')}>CANCELAR</button><button className="button button--dark" disabled={loading || generatingSku || !data || form.variants.length === 0}>{loading ? 'SALVANDO...' : 'SALVAR PRODUTO'}</button></div>
  </form></div>
}
